/**
 * CollectionGroupDetail - Redesigned Collection Group Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Collapsible sections
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	NumberBox,
	TextBox,
} from '../../components/base';
import { EditModal, EditPageField } from '../../components/container';
import { ImageBox } from '../../components/base/ImageBox';
import ComboBoxCollectionType from '../../components/shared/hoc-collection-type/ComboBoxCollectionType';
import {
	CollectionGroupModel,
	dfCollectionGroupModel,
	validCollectionGroupSchema,
} from '../../context/models/collections/CollectionGroupModel';
import { Layers, Image as ImageIcon, Info, Settings } from 'lucide-react';

type ICollectionGroupDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: CollectionGroupModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: CollectionGroupModel) => void;
};

const CollectionGroupDetail = (props: ICollectionGroupDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<CollectionGroupModel>(dfCollectionGroupModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CollectionGroupModel>({
		resolver: yupResolver(validCollectionGroupSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: CollectionGroupModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof CollectionGroupModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Basic Info
	const basicInfoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Loại bộ sưu tập'
				required
				error={errors.type?.message}
			>
				<ComboBoxCollectionType
					name='type'
					error={errors.type?.message}
					onValueChanged={(value) => updateField('type', value)}
					value={initialValues.type}
					hasValid={true}
					placeholder='Chọn loại...'
				/>
			</EditPageField>

			<EditPageField
				label='Số thứ tự'
				error={errors.stt?.message}
				helpText='Thứ tự hiển thị trong danh sách'
			>
				<NumberBox
					name='stt'
					error={errors.stt?.message}
					onValueChanged={(value) => updateField('stt', value)}
					value={initialValues.stt}
					hasValid={true}
				/>
			</EditPageField>

			<EditPageField
				label='Tên nhóm'
				required
				error={errors.name?.message}
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên nhóm...'
				/>
			</EditPageField>

			<EditPageField
				label='Tiêu đề'
				error={errors.title?.message}
			>
				<TextBox
					name='title'
					error={errors.title?.message}
					onValueChanged={(value) => updateField('title', value)}
					value={initialValues.title}
					hasValid={true}
					placeholder='Tiêu đề hiển thị...'
				/>
			</EditPageField>

			<EditPageField
				label='Sapo'
				error={errors.sapo?.message}
				helpText='Mô tả ngắn cho nhóm bộ sưu tập'
			>
				<TextBox
					name='sapo'
					error={errors.sapo?.message}
					onValueChanged={(value) => updateField('sapo', value)}
					value={initialValues.sapo}
					hasValid={true}
					placeholder='Mô tả ngắn...'
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Media
	const mediaTab = (
		<div className='space-y-6'>
			<EditPageField
				label='Icon'
				error={errors.icon?.message}
				helpText='Icon thu nhỏ cho nhóm bộ sưu tập'
			>
				<ImageBox
					name='icon'
					isDisabled={initialValues.name.length <= 0}
					file_name={initialValues.name}
					value={initialValues.icon}
					onDataChanged={(icon) => {
						updateField('icon', icon?.path || '');
					}}
				/>
			</EditPageField>

			<EditPageField
				label='Hình ảnh'
				error={errors.image?.message}
				helpText='Hình ảnh đại diện cho nhóm bộ sưu tập'
			>
				<ImageBox
					name='image'
					isDisabled={initialValues.name.length <= 0}
					file_name={initialValues.name}
					value={initialValues.image}
					onDataChanged={(image) => {
						updateField('image', image?.path || '');
					}}
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'basic',
			label: 'Thông tin cơ bản',
			icon: <Layers className='h-4 w-4' />,
			content: basicInfoTab,
		},
		{
			id: 'media',
			label: 'Hình ảnh',
			icon: <ImageIcon className='h-4 w-4' />,
			content: mediaTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới nhóm bộ sưu tập' : 'Chỉnh sửa nhóm bộ sưu tập'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Nhóm bộ sưu tập', href: '/collections-group' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default CollectionGroupDetail;
